import * as t from 'io-ts';

import {RContributingSegmentsOptionsDirty} from './analyticsMetricDetailsModel';
import {RAnalyticsMetricsKeysDirty} from './analyticsMetricsModel';
import {RAnalyticsSavedViewFiltersDirty} from './analyticsSavedViewModel';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';

/**
 * Analytics models and parsers for exporting.
 */

/*
 * Runtime type.
 */

const RAnalyticsColumnNamesApiDirty = t.readonly(
  t.type({
    columns: t.type({
      messages: t.readonlyArray(t.string),
      activities: t.readonlyArray(t.string),
      // TODO: We can clean this type up when the backend change has actually landed.
      csat: t.union([t.readonlyArray(t.string), t.undefined]),
    }),
  }),
);

/** Selectable export types. */
export enum AsyncExportTypesEnum {
  ACTIVITIES = 'activities',
  MESSAGES = 'messages',
  CSAT = 'csat',
}

export enum EventTypesEnum {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
  OUT_REPLY = 'out_reply',
  ARCHIVE = 'archive',
  REOPEN = 'reopen',
  REMINDER = 'reminder',
  TRASH = 'trash',
  RESTORE = 'restore',
  ASSIGN = 'assign',
  UNASSIGN = 'unassign',
  TAG = 'tag',
  UNTAG = 'untag',
  MOVE = 'move',
  COMMENT = 'comment',
  MENTION = 'mention',
  SNOOZE = 'snooze',
}

export const RAnalyticsColumnNamesDirty = t.readonly(
  t.type({
    messages: t.readonlyArray(t.string),
    activities: t.readonlyArray(t.string),
    csat: t.readonlyArray(t.string),
  }),
);

const RAnalyticsEventTypesApiDirty = t.readonly(
  t.type({
    event_types: t.readonlyArray(makeEnumRuntimeType(AsyncExportTypesEnum)),
  }),
);

const RAnalyticsEventTypesDirty = t.readonlyArray(t.string);

export const RAnalyticsExportApiDirty = t.readonly(
  t.type({
    id: t.union([t.undefined, t.Integer]),
    url: t.string,
    status: t.string,
    progress: t.number,
    requester: t.string,
    start: t.number,
    end: t.number,
    size: t.union([t.number, t.null]),
    export_type: makeEnumRuntimeType(AsyncExportTypesEnum),
    filename: t.string,
    download_url: t.string,
    created_at: t.number,
  }),
);

const RAnalyticsExportsApiDirty = t.readonly(
  t.type({
    exports: t.readonlyArray(RAnalyticsExportApiDirty),
  }),
);

export const RAnalyticsExportDirty = t.readonly(
  t.type({
    id: t.union([t.undefined, t.Integer]),
    url: t.string,
    status: t.string,
    progress: t.number,
    requester: t.string,
    start: t.number,
    end: t.number,
    size: t.union([t.number, t.null]),
    exportType: t.string,
    filename: t.string,
    downloadUrl: t.string,
    createdAt: t.number,
  }),
);

export const RAnalyticsExportsDirty = t.readonlyArray(RAnalyticsExportDirty);

/** This POST response is not currently used. */
const RAnalyticsCreateExportApiDirty = RAnalyticsExportApiDirty;

/** These are options for the request. */
const RAnalyticsCreateExportOptionsDirty = t.readonly(
  t.intersection([
    t.type({
      export_type: makeEnumRuntimeType(AsyncExportTypesEnum),
      sep: t.string,
      columns: t.readonlyArray(t.string),
      event_types: t.readonlyArray(t.string),
      filters: RAnalyticsSavedViewFiltersDirty,
    }),
    t.partial({
      metric_details: t.type({
        selected_metric: t.union([t.undefined, RAnalyticsMetricsKeysDirty]),
        options: t.union([t.undefined, RContributingSegmentsOptionsDirty]),
      }),
    }),
  ]),
);

/*
 * Type.
 */

export interface AnalyticsColumnNamesApiModel extends t.TypeOf<typeof RAnalyticsColumnNamesApiDirty> {}
export interface AnalyticsColumnNamesModel extends t.TypeOf<typeof RAnalyticsColumnNamesDirty> {}

export interface AnalyticsEventTypesApiModel extends t.TypeOf<typeof RAnalyticsEventTypesApiDirty> {}
export interface AnalyticsEventTypesModel extends t.TypeOf<typeof RAnalyticsEventTypesDirty> {}

export interface AnalyticsExportModel extends t.TypeOf<typeof RAnalyticsExportDirty> {}
export interface AnalyticsExportsModel extends t.TypeOf<typeof RAnalyticsExportsDirty> {}
export interface AnalyticsExportApiModel extends t.TypeOf<typeof RAnalyticsExportApiDirty> {}
export interface AnalyticsExportsApiModel extends t.TypeOf<typeof RAnalyticsExportsApiDirty> {}

export interface AnalyticsCreateExportApiModel extends t.TypeOf<typeof RAnalyticsCreateExportApiDirty> {}
export interface AnalyticsCreateExportOptionsModel
  extends t.TypeOf<typeof RAnalyticsCreateExportOptionsDirty> {}

/*
 * IO methods.
 */

function parseAnalyticsColumnNames(src: AnalyticsColumnNamesApiModel): AnalyticsColumnNamesModel {
  return {
    ...src.columns,
    [AsyncExportTypesEnum.CSAT]: src.columns.csat ?? [],
  };
}

function parseAnalyticsEventTypes(src: AnalyticsEventTypesApiModel): AnalyticsEventTypesModel {
  return src.event_types;
}

export function parseAnalyticsExport(src: AnalyticsExportApiModel): AnalyticsExportModel {
  return {
    id: src.id,
    url: src.url,
    status: src.status,
    progress: src.progress,
    requester: src.requester,
    start: src.start,
    end: src.end,
    size: src.size,
    exportType: src.export_type,
    filename: src.filename,
    downloadUrl: src.download_url,
    createdAt: src.created_at,
  };
}

function parseAnalyticsExports(src: AnalyticsExportsApiModel): AnalyticsExportsModel {
  const {exports} = src;

  return exports.map(parseAnalyticsExport);
}

function parseAnalyticsCreateExport(src: AnalyticsCreateExportApiModel): AnalyticsCreateExportApiModel {
  return src;
}

/*
 * API type.
 */

export const ApiAnalyticsColumnNames = makeApiType(
  'analyticsColumnNames',
  RAnalyticsColumnNamesDirty,
  parseAnalyticsColumnNames,
);

export const ApiAnalyticsEventTypes = makeApiType(
  'analyticsEventTypes',
  RAnalyticsEventTypesDirty,
  parseAnalyticsEventTypes,
);

export const ApiAnalyticsExports = makeApiType(
  'analyticsExports',
  RAnalyticsExportsDirty,
  parseAnalyticsExports,
);

export const ApiCreateAnalyticsExport = makeApiType(
  'analyticsCreateExport',
  RAnalyticsCreateExportApiDirty,
  parseAnalyticsCreateExport,
);
